import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import type { NotificationsResp, UserNotification, NotificationType } from '../hooks/useMyNotifications';

interface NotificationsContextValue {
  data: NotificationsResp | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markUnread: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  bulkAction: (ids: string[], action: 'read' | 'unread' | 'delete') => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

/**
 * Single-source-of-truth provider for portal notifications.
 *
 * Lives at the App level so both the public Header bell and the
 * CustomerLayout sidebar badge share the same state without double-fetching.
 * Only fetches when the user is authenticated as a 'cliente'.
 */
export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const isCliente = isAuthenticated && user?.ruolo === 'cliente';

  const [data, setData] = useState<NotificationsResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<NotificationsResp>('/api/clienti/me/notifications?limit=100');
      setData(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore caricamento avvisi');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount/login, reset on logout
  useEffect(() => {
    if (isCliente) {
      reload();
    } else {
      setData(null);
      setError(null);
    }
  }, [isCliente, reload]);

  const markRead = useCallback(async (id: string) => {
    await api.patch(`/api/clienti/me/notifications/${id}/read`);
    await reload();
  }, [reload]);

  const markUnread = useCallback(async (id: string) => {
    await api.patch(`/api/clienti/me/notifications/${id}/unread`);
    await reload();
  }, [reload]);

  const markAllRead = useCallback(async () => {
    await api.patch('/api/clienti/me/notifications/read-all');
    await reload();
  }, [reload]);

  const bulkAction = useCallback(async (ids: string[], action: 'read' | 'unread' | 'delete') => {
    await api.post('/api/clienti/me/notifications/bulk', { ids, action });
    await reload();
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    await api.delete(`/api/clienti/me/notifications/${id}`);
    await reload();
  }, [reload]);

  return (
    <NotificationsContext.Provider value={{ data, loading, error, reload, markRead, markUnread, markAllRead, bulkAction, remove }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within <NotificationsProvider>');
  }
  return ctx;
}

/** Safe version — returns null if outside the provider (e.g. public pages). */
export function useNotificationsOptional(): NotificationsContextValue | null {
  return useContext(NotificationsContext);
}

export type { UserNotification, NotificationType, NotificationsResp };
